/**
 * Produtos e serviços em destaque da loja - card #218, contrato do spec 033.
 *
 * Arquivo próprio, e não mais um bloco em `merchant.ts`: quatro repositórios
 * escrevem contra o mesmo contrato ao mesmo tempo, e um arquivo só para ele
 * deixa óbvio o que mudou quando o contrato mudar.
 */

/**
 * Teto de itens por loja (§3 do spec 033).
 *
 * Quem recusa de verdade é o core, com `BusinessException` - não há restrição
 * no banco. Aqui o número existe para a tela avisar ANTES de o lojista
 * preencher um formulário inteiro para receber um 400 no fim.
 *
 * O teto é regra de produto, não limite técnico: uma loja com 60 "destaques"
 * não tem destaque nenhum.
 */
export const FEATURED_ITEM_MAX_COUNT = 20;

/** `varchar(80)` - o que aparece em negrito no cartão. */
export const FEATURED_ITEM_NAME_MAX_LENGTH = 80;

/** `varchar(200)` - curta de propósito: é vitrine, não cardápio. */
export const FEATURED_ITEM_DESCRIPTION_MAX_LENGTH = 200;

/** `numeric(12,2)` com `check price is null or (price > 0 and price <= 1000000)`. */
export const FEATURED_ITEM_PRICE_MAX = 1_000_000;

/**
 * O que a tela manda ao criar ou editar um item.
 *
 * **`price` é `number | null`, nunca `number`.** Item sem preço tem de chegar
 * ao core como `null` - barbearia cadastra "Corte + barba" sem valor fixo, e
 * um `0` aqui vira "R$ 0,00" na página pública, que é afirmação falsa (§2 do
 * spec 033; mesma proibição do card #184). Nenhum cliente pode fazer
 * `price ?? 0`.
 */
export interface MerchantFeaturedItemRequestDto {
  name: string;
  description: string | null;
  imageUrl: string | null;
  price: number | null;
}

/** O item como o core o devolve: o pedido mais `id` e `position`. */
export interface MerchantFeaturedItemDto {
  id: string;
  name: string;
  description: string | null;
  imageUrl: string | null;
  price: number | null;
  /** 0-based, contígua. Só o painel a enxerga - o DTO público não a publica. */
  position: number;
}

/**
 * Corpo do `PUT /order`: a lista **inteira** de ids, na ordem nova.
 *
 * Não é um "sobe/desce": arrastar um item do fim para o começo viraria N
 * chamadas, e cada uma é uma chance de a tela e o banco discordarem no meio.
 * O core recusa a lista que não contenha exatamente os ids vivos da loja.
 */
export interface MerchantFeaturedItemOrderRequestDto {
  ids: string[];
}

/**
 * Copy para os `errorCode`s que o core devolve ao recusar um item em destaque.
 *
 * **As chaves são os códigos que o `MerchantFeaturedItemService` de fato emite**
 * - conferidas no core depois que as duas fatias do #218 ficaram prontas. Não
 * são um palpite pela convenção do `EXTERNAL_LINK_ERROR_MESSAGES`: chave errada
 * aqui não quebra nada visivelmente, ela só faz o `extractErrorMessage` cair na
 * mensagem crua do servidor, e o lojista lê "A loja pode ter no máximo 20 itens
 * em destaque" em vez de "Remova um para incluir outro". Defeito silencioso, e
 * é por isso que estas três strings valem a conferência.
 *
 * `NOT_FOUND` não entra: o core responde 404 com `NotFoundException`, que não
 * carrega `errorCode`. A tela trata pelo status, não por código.
 */
export const FEATURED_ITEM_ERROR_MESSAGES: Record<string, string> = {
  MERCHANT_FEATURED_ITEM_LIMIT_REACHED: `Você já tem ${FEATURED_ITEM_MAX_COUNT} itens em destaque. Remova um para incluir outro.`,
  MERCHANT_FEATURED_ITEM_ORDER_MISMATCH:
    'A lista mudou enquanto você reordenava. Atualizamos a ordem salva na tela.',
};
